// รายชื่อรายห้องของหน้า "ติดตามงานครูประจำชั้นศาสนา" — โหลดเมื่อกางแถวเท่านั้น
//
// แยกไฟล์ออกมาเพราะสามงานคืนคนละชุดคอลัมน์ ถ้าอยู่ในหน้าหลักด้วยจะกลายเป็นไฟล์เดียว
// ที่มีสามตารางซ้อนอยู่ข้างในแถว แล้วอ่านไม่ออกว่าตรงไหนเป็นของงานไหน
// (คู่ขนานกับ reports/AdvisorTaskDetail.js ของภาคสามัญ)
import React, { useEffect, useState } from 'react'
import { Badge, OverlayTrigger, Table, Tooltip } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import axiosInstance from '../services/axiosInterceptor'
import { useLanguage } from '../contexts/LanguageContext'
import { StudentName, studentDisplayName } from '../utils/studentName'
import { API, LevelBadge, StudentAvatar, alertRequestError, thaiDate } from './academicShared'

// ชื่อ + เลขประจำตัว + รูป — ชุดเดียวกันทั้งสามตาราง
const StudentCell = ({ row, lang, onClick }) => (
  <div className="d-flex align-items-center gap-2" style={{ cursor: 'pointer' }} onClick={onClick}>
    <StudentAvatar photo={row.st_photo} name={studentDisplayName(row, lang)} size={28} />
    <div style={{ minWidth: 0 }}>
      <div className="fs-sm text-truncate"><StudentName student={row} lang={lang} /></div>
      <div className="fs-xs text-secondary">{row.st_idnumber}</div>
    </div>
  </div>
)

// ---- หอพัก ----
// คนที่ยังไม่มีทะเบียนหอขึ้นก่อน (server เรียงมาแล้ว) และใช้ป้ายสีแดง ไม่ใช่ขีดกลางเปล่า ๆ
// ที่อ่านเหมือนข้อมูลหาย — โรงเรียนเป็นแบบประจำ ทุกคนต้องมีหอ
const DormTable = ({ t, rows, lang, go }) => (
  <Table className="table-sm mb-0 align-middle table-hover">
    <thead>
      <tr>
        <th style={{ width: 48 }}>#</th>
        <th>{t.academic.advisorTasks.colStudent}</th>
        <th style={{ width: 200 }}>{t.academic.advisorTasks.colDormName}</th>
        <th style={{ width: 120 }}>{t.academic.advisorTasks.colCheckin}</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r, i) => (
        <tr key={r.st_id}>
          <td className="text-secondary fs-xs">{i + 1}</td>
          <td><StudentCell row={r} lang={lang} onClick={() => go(r.st_id)} /></td>
          <td className="fs-sm">
            {r.dorm_id
              ? <span>{r.village_name ? `${r.village_name} · ` : ''}{r.dorm_name}</span>
              : <Badge bg="danger" className="fs-xs">{t.academic.advisorTasks.noDorm}</Badge>}
          </td>
          <td className="fs-sm">{r.checkin_date ? thaiDate(r.checkin_date) : <span className="text-secondary">-</span>}</td>
        </tr>
      ))}
    </tbody>
  </Table>
)

// ---- ขั้นตอนดูแลนักเรียน ----
// แสดงเฉพาะขั้นของครูประจำชั้น (server กรอง who === 'advisor' มาแล้ว) — ขั้นของหัวหน้าระดับ
// ไม่ใช่งานของเขา เอามาแสดงจะอ่านเหมือนเขายังทำไม่ครบทั้งที่รอคนอื่นอยู่
const StepBadge = ({ t, s }) => {
  const body = (
    <Badge bg={s.done ? 'success' : 'danger'} className="fs-xs fw-normal">
      {s.done ? <i className="ri-check-line me-1" /> : <i className="ri-time-line me-1" />}
      {s.short}
    </Badge>
  )
  if (!s.done) {
    return (
      <OverlayTrigger overlay={<Tooltip>{s.label}</Tooltip>}>
        <span>{body}</span>
      </OverlayTrigger>
    )
  }
  const when = s.done_at ? thaiDate(String(s.done_at).slice(0, 10)) : ''
  return (
    <OverlayTrigger
      overlay={<Tooltip>{t.academic.advisorTasks.stepDoneBy(s.done_by_name || t.careTracking.noRecorder, when)}</Tooltip>}
    >
      <span>{body}</span>
    </OverlayTrigger>
  )
}

const CareTable = ({ t, rows, lang, go }) => (
  <Table className="table-sm mb-0 align-middle table-hover">
    <thead>
      <tr>
        <th style={{ width: 48 }}>#</th>
        <th>{t.academic.advisorTasks.colStudent}</th>
        <th style={{ width: 110 }}>{t.academic.advisorTasks.colRisk}</th>
        <th style={{ width: 220 }}>{t.academic.advisorTasks.colWhy}</th>
        <th>{t.academic.advisorTasks.colSteps}</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r, i) => (
        <tr key={r.st_id}>
          <td className="text-secondary fs-xs">{i + 1}</td>
          <td><StudentCell row={r} lang={lang} onClick={() => go(r.st_id)} /></td>
          <td><LevelBadge level={r.overall}>{t.risk[r.overall]}</LevelBadge></td>
          <td className="fs-xs">
            {r.attendance.level !== 'green' && (
              <div>{t.risk.axisAttendance}: {t.academic.advisorTasks.absentOf(r.attendance.absent, r.attendance.total, r.attendance.absenceRate)}</div>
            )}
            {r.behavior.level !== 'green' && (
              <div>{t.risk.axisBehavior}: {t.academic.advisorTasks.conductLeft(r.behavior.remaining)}</div>
            )}
          </td>
          <td>
            <div className="d-flex flex-wrap gap-1">
              {r.steps.map(s => <StepBadge key={`${s.axis}-${s.step}`} t={t} s={s} />)}
            </div>
          </td>
        </tr>
      ))}
    </tbody>
  </Table>
)

// ---- ใบลาที่ครูประจำชั้นศาสนาแจ้ง ----
const LeaveTable = ({ t, rows, lang, go }) => (
  <Table className="table-sm mb-0 align-middle table-hover">
    <thead>
      <tr>
        <th style={{ width: 48 }}>#</th>
        <th>{t.academic.advisorTasks.colStudent}</th>
        <th style={{ width: 90 }}>{t.academic.advisorTasks.colLeaveType}</th>
        <th style={{ width: 190 }}>{t.academic.advisorTasks.colLeaveDates}</th>
        <th>{t.academic.advisorTasks.colNote}</th>
        <th style={{ width: 160 }}>{t.academic.advisorTasks.colReportedBy}</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r, i) => (
        <tr key={r.leave_id}>
          <td className="text-secondary fs-xs">{i + 1}</td>
          <td><StudentCell row={r} lang={lang} onClick={() => go(r.st_id)} /></td>
          <td className="fs-sm">{t.academic.leave.type[r.leave_type] || r.leave_type}</td>
          <td className="fs-sm">
            {r.leave_start === r.leave_end
              ? thaiDate(r.leave_start)
              : `${thaiDate(r.leave_start)} – ${thaiDate(r.leave_end)}`}
          </td>
          <td className="fs-sm">{r.leave_note || <span className="text-secondary">-</span>}</td>
          <td className="fs-sm text-truncate">{r.reported_by || <span className="text-secondary">-</span>}</td>
        </tr>
      ))}
    </tbody>
  </Table>
)

const TABLES = { dorm: DormTable, care: CareTable, leave: LeaveTable }

export default function IslamicAdvisorTaskDetail({ t, yearId, irId, duty }) {
  const { lang } = useLanguage()
  const navigate = useNavigate()
  // เก็บ "แถวชุดนี้เป็นของ ปี|ห้อง|แท็บ ไหน" ไว้คู่กับตัวแถวเสมอ ไม่ใช่ rows เดี่ยว ๆ
  //
  // พอสลับแท็บ React จะ render รอบหนึ่งด้วย duty ใหม่ + แถวชุดเก่า **ก่อน** effect ได้ทำงาน
  // ตารางของงานใหม่จึงได้แถวของงานเดิมไปวาด แล้วพังตรงคอลัมน์ที่อีกงานไม่มี (ไม่ใช่แค่
  // แสดงผิด — มันโยน TypeError ทั้งหน้า) เทียบคีย์ก่อนวาดจึงตัดปัญหาทั้งกลุ่มนี้ทิ้ง
  // รวมถึงคำตอบที่มาถึงช้ากว่าคำขอที่ยิงทีหลัง
  const [state, setState] = useState({ key: null, rows: null })

  useEffect(() => {
    if (!yearId || !irId || !duty) return
    let alive = true
    const key = `${yearId}|${irId}|${duty}`
    setState({ key, rows: null })
    axiosInstance.get(`${API}/islamic/academic/advisorTasks/${yearId}/${duty}/${irId}`)
      .then(res => { if (alive) setState({ key, rows: Array.isArray(res.data) ? res.data : [] }) })
      .catch(err => { if (alive) { setState({ key, rows: [] }); alertRequestError(t, err, t.common.error) } })
    return () => { alive = false }
  }, [yearId, irId, duty, t])

  // แถวยังเป็นของแท็บ/ห้องก่อนหน้า = ยังโหลดไม่เสร็จ ไม่ใช่ข้อมูลของแท็บนี้
  const rows = state.key === `${yearId}|${irId}|${duty}` ? state.rows : null

  if (rows === null) {
    return <div className="text-center text-secondary py-4 fs-sm">{t.academic.advisorTasks.loading}</div>
  }
  if (rows.length === 0) {
    return <div className="text-center text-secondary py-4 fs-sm">{t.academic.advisorTasks.detailEmpty}</div>
  }

  const Body = TABLES[duty]
  return (
    <div className="table-responsive">
      <Body t={t} rows={rows} lang={lang} go={(stId) => navigate(`/islamic/student-tracking/${stId}`)} />
    </div>
  )
}
